// Avoid importing at the top level to prevent potential circular dependencies
// Instead, import inside the functions
import { Vendor } from '../../types/vendor';
import { getAuth } from 'firebase/auth';

// Base URL can be configured based on environment
const API_BASE_URL =  'https://wedify.be'

const STATIC_TOKEN = 'j/w1pkmuJdeL5MslaCM6cISLG/rur+E/Pt9/mHtrdBc=';

// Don't initialize during module loading
// Instead, get a fresh instance each time a function is called

export const FavoritesAPI = {
  /**
   * Adds a vendor to the user's favorites.
   * @param {string} vendorId - The ID of the vendor to favorite.
   * @returns {Promise<Response>} - The raw fetch response.
   */
  addVendorToFavorites: async (vendorId: string): Promise<Response> => {
    if (!vendorId) {
      throw new Error('Vendor ID is required to add to favorites');
    }
    
    try {
      // Get current user ID
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        console.error('FAVORITES API: No user is logged in');
        throw new Error('User must be logged in to add favorites');
      }
      
      const externalId = currentUser.uid;
      console.log('FAVORITES API: Current User ID:', externalId);
      
      // Format exactly as per curl example
      // curl --location --request POST 'http://localhost:8080/api/vendor-favorites/23'
      const endpoint = `${API_BASE_URL}/api/vendor-favorites/${vendorId}`;
      console.log('FAVORITES API: Calling endpoint:', endpoint);
      
      // Headers based on curl example
      const headers = {
        'X-Auth-Token': STATIC_TOKEN,
        'authenticationService': 'firebase',
        'externalClientId': externalId
      };
      
      console.log('FAVORITES API: Request headers:', JSON.stringify(headers, null, 2));
      
      // Make the request
      console.log('FAVORITES API: Sending POST request...');
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        // Empty body as per curl example
      });
      
      console.log('FAVORITES API: Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`FAVORITES API: Failed to add vendor to favorites: ${response.status}`, errorText);
        throw new Error(`API Error: ${response.status}`);
      }
      
      console.log('FAVORITES API: Successfully added vendor to favorites:', vendorId);
      return response; 
    } catch (error) {
      console.error('FAVORITES API: Error adding vendor to favorites:', error);
      throw error;
    }
  },

  /**
   * Removes a vendor from the user's favorites.
   * @param {string} vendorId - The ID of the vendor to unfavorite.
   * @returns {Promise<Response>} - The raw fetch response.
   */
  removeVendorFromFavorites: async (vendorId: string): Promise<Response> => {
    if (!vendorId) {
      throw new Error('Vendor ID is required to remove from favorites');
    }
    
    try {
      // Get current user ID
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        console.error('FAVORITES API: No user is logged in');
        throw new Error('User must be logged in to remove favorites');
      }
      
      const externalId = currentUser.uid;
      console.log('FAVORITES API: Current User ID:', externalId);
      
      const endpoint = `${API_BASE_URL}/api/vendor-favorites/${vendorId}`;
      console.log('FAVORITES API: Calling endpoint:', endpoint);
      
      const headers = {
        'X-Auth-Token': STATIC_TOKEN,
        'authenticationService': 'firebase',
        'externalClientId': externalId
      };
      
      console.log('FAVORITES API: Request headers:', JSON.stringify(headers, null, 2));
      
      // Make the request
      console.log('FAVORITES API: Sending DELETE request...');
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers,
      });
      
      console.log('FAVORITES API: Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`FAVORITES API: Failed to remove vendor from favorites: ${response.status}`, errorText);
        throw new Error(`API Error: ${response.status}`);
      }
      
      console.log('FAVORITES API: Successfully removed vendor from favorites:', vendorId);
      return response; 
    } catch (error) {
      console.error('FAVORITES API: Error removing vendor from favorites:', error);
      throw error;
    }
  },

  /**
   * Gets the user's favorite vendors.
   * @param {number} page - The page number for pagination (defaults to 0).
   * @returns {Promise<Vendor[]>} - An array of favorite Vendor objects.
   */
  getFavoriteVendors: async (page: number = 0): Promise<Vendor[]> => {
    try {
      console.log('FAVORITES API: Getting favorite vendors, page:', page);
      
      // Get current user ID
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        console.error('FAVORITES API: No user is logged in');
        throw new Error('User must be logged in to view favorites');
      }
      
      const externalId = currentUser.uid;
      console.log('FAVORITES API: Current User ID:', externalId);
      
      // Updated format to match POST structure, adding page as a query param
      const endpoint = `${API_BASE_URL}/api/vendor-favorites?page=${page}`;
      console.log('FAVORITES API: Calling endpoint:', endpoint);
      
      // Headers - match the same format as the POST method
      const headers = {
        'X-Auth-Token': STATIC_TOKEN,
        'authenticationService': 'firebase',
        'externalClientId': externalId
      };
      
      console.log('FAVORITES API: Request headers:', JSON.stringify(headers, null, 2));
      
      // Make the request
      console.log('FAVORITES API: Sending GET request...');
      const response = await fetch(endpoint, {
        method: 'GET',
        headers
      });
      
      console.log('FAVORITES API: Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`FAVORITES API: Failed to get favorite vendors: ${response.status}`, errorText);
        throw new Error(`API Error: ${response.status}`);
      }

      // Check if the response body is empty or not JSON
      const text = await response.text();
      console.log('FAVORITES API: Response received with length:', text.length);
      
      if (!text) {
        console.log('FAVORITES API: Empty response, returning empty array');
        return []; // Return empty array if response body is empty
      }

      try {
        const data = JSON.parse(text);
        console.log('FAVORITES API: Successfully parsed response, found items:', Array.isArray(data) ? data.length : 'non-array response');
        return Array.isArray(data) ? data as Vendor[] : [];
      } catch (jsonError) {
        console.error('FAVORITES API: Failed to parse favorite vendors response:', jsonError);
        return []; // Return empty array on parse error
      }

    } catch (error) {
      console.error('FAVORITES API: Error getting favorite vendors:', error);
      return []; // Return empty array on any error
    }
  },

  /**
   * Deletes a vendor from the platform.
   * @param {string} vendorId - The ID of the vendor to delete.
   * @returns {Promise<Response>} - The raw fetch response.
   */
  deleteVendor: async (vendorId: string): Promise<Response> => {
    if (!vendorId) {
      throw new Error('Vendor ID is required to delete a vendor');
    }
    
    try {
      // Get current user ID
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        console.error('FAVORITES API: No user is logged in');
        throw new Error('User must be logged in to delete a vendor');
      }
      
      const externalId = currentUser.uid;
      console.log('FAVORITES API: Current User ID:', externalId);
      
      // Format based on provided curl example
      const endpoint = `${API_BASE_URL}/api/vendors/${vendorId}/favorite?authService=firebase&externalId=${externalId}`;
      console.log('FAVORITES API: Calling endpoint:', endpoint);
      
      // Headers based on curl example
      const headers = {
        'X-Auth-Token': STATIC_TOKEN
      };
      
      console.log('FAVORITES API: Request headers:', JSON.stringify(headers, null, 2));
      
      // Make the request
      console.log('FAVORITES API: Sending DELETE request...');
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers
      });
      
      console.log('FAVORITES API: Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`FAVORITES API: Failed to delete vendor: ${response.status}`, errorText);
        throw new Error(`API Error: ${response.status}`);
      }
      
      console.log('FAVORITES API: Successfully deleted vendor:', vendorId);
      return response; 
    } catch (error) {
      console.error('FAVORITES API: Error deleting vendor:', error);
      throw error;
    }
  },
}; 